import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import authService from '../../services/authService';
import './LoginPage.css';

const schema = yup.object({
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one digit')
    .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
}).required();

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [isVerifying, setIsVerifying] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        setIsTokenValid(false);
        return;
      }

      try {
        const response = await authService.verifyResetToken(token);
        setIsTokenValid(response.data?.data === true || response.data === true);
      } catch (err) {
        setIsTokenValid(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await authService.resetPassword(token, data.password);
      setIsSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state while verifying token
  if (isVerifying) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-loading">Verifying reset link...</div>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!isTokenValid) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="reset-icon error">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <h1 className="login-title">Invalid or Expired Link</h1>
            <p className="login-subtitle">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
          </div>

          <Link to="/forgot-password" className="login-button" style={{ textAlign: 'center', display: 'block', textDecoration: 'none' }}>
            Request New Reset Link
          </Link>

          <div className="login-form-links" style={{ marginTop: '1rem' }}>
            <Link to="/login" className="forgot-password-link">
              Back to Login
            </Link>
          </div>

          <div className="login-footer">
            <p>&copy; {new Date().getFullYear()} Touchline Memories. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSubmitted) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <div className="reset-icon success">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="login-title">Password Reset Successful</h1>
            <p className="login-subtitle">
              Your password has been reset successfully. You can now log in with your new password.
            </p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="login-button"
          >
            Go to Login
          </button>

          <div className="login-footer">
            <p>&copy; {new Date().getFullYear()} Touchline Memories. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Set New Password</h1>
          <p className="login-subtitle">
            Enter your new password below.
          </p>
        </div>

        <form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          {error && (
            <div className="login-form-error" role="alert">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              New Password
            </label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`form-input ${errors.password ? 'form-input-error' : ''}`}
                placeholder="Enter new password"
                autoComplete="new-password"
                disabled={isSubmitting}
                {...register('password')}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.password && (
              <span className="form-error-message" role="alert">
                {errors.password.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              className={`form-input ${errors.confirmPassword ? 'form-input-error' : ''}`}
              placeholder="Confirm new password"
              autoComplete="new-password"
              disabled={isSubmitting}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <span className="form-error-message" role="alert">
                {errors.confirmPassword.message}
              </span>
            )}
          </div>

          <div className="password-requirements">
            <p>Password must contain:</p>
            <ul>
              <li>At least 8 characters</li>
              <li>One uppercase letter</li>
              <li>One lowercase letter</li>
              <li>One digit</li>
              <li>One special character</li>
            </ul>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Resetting...' : 'Reset Password'}
          </button>

          <div className="login-form-links">
            <Link to="/login" className="forgot-password-link">
              Back to Login
            </Link>
          </div>
        </form>

        <div className="login-footer">
          <p>&copy; {new Date().getFullYear()} Touchline Memories. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
